import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'

import Button from '~/components/Buttons/Button'
import InputGroup from '~/components/Inputs/InputGroup'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import RequiredInformation from '~/components/RequiredInformation'
import { FORM_ERRORS } from '~/const'
import { keys as rolesKeys, useUpdateRoleMutation } from '~/queries/user-roles'

interface FormValues {
  description: string
  name: string
}

const validationSchema = Yup.object().shape({
  description: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
  name: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
})

interface FormProps {
  description?: string
  id: string
  name: string
  onClose: VoidFunction
  onFailure: (e: unknown) => void
  onSuccess: VoidFunction
}

const RoleEditForm = ({ description, id, name, onClose, onFailure, onSuccess }: FormProps): ReactElement => {
  /** Local state */

  const defaultValues = { description: description || '', name }

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
  } = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema),
  })

  const queryClient = useQueryClient()

  /** Queries & Mutations */

  const { mutateAsync: updateRole } = useUpdateRoleMutation()

  /** Handlers */

  const handleFormSubmit = async (values: FormValues) => {
    const { description: updatedDescription, name: updatedName } = values

    try {
      if (updatedDescription !== description || updatedName !== name) {
        await updateRole({ description: updatedDescription, id: id, name: updatedName })
        await queryClient.invalidateQueries({ queryKey: rolesKeys.details(id) })
        await queryClient.invalidateQueries({ queryKey: rolesKeys.list() })
        onSuccess()
      }
    } catch (e) {
      onFailure(e)
    }
  }

  /** Render */

  return (
    <form className="general-information-form" onSubmit={handleSubmit(handleFormSubmit)}>
      <ModalBody>
        <RequiredInformation />
        <InputGroup>
          <TextInput
            {...register('name')}
            autoFocus
            error={errors.name?.message}
            label="Name"
            placeholder="Enter name"
          />
          <TextInput
            {...register('description')}
            error={errors.description?.message}
            label="Description"
            placeholder="Enter description"
          />
        </InputGroup>
      </ModalBody>
      <ModalFooter>
        <Button disabled={isSubmitting} ghost onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={isSubmitting} type="submit">
          {isSubmitting ? 'Loading...' : 'Save'}
        </Button>
      </ModalFooter>
    </form>
  )
}

interface ModalProps extends FormProps {
  isOpen: boolean
}

const ModalRoleEdit = ({ isOpen, onClose, ...props }: ModalProps): ReactElement => {
  return (
    <Modal className="role-edit-modal" isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>Edit role information</ModalHeader>
      <RoleEditForm {...props} onClose={onClose} />
    </Modal>
  )
}

export default ModalRoleEdit
